import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  OpenInNew as OpenIcon,
  CheckCircleOutline as CheckIcon,
  HighlightOff as DetachIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import { apiGetContactsCollection, apiUpdateContact } from "../../../graphQL/addressbook/collections/contacts";
import { apiUpdatePropertyAsset } from "../../../graphQL/properties/collections/propertyAssets";

const TRUSTED_SOURCES = new Set([
  "san_mateo_arcgis",
  "county_assessor",
  "county_clerk",
  "user_verified",
  "john_filemaker_2026_03_10",
  "filemaker",
  "aptotude",
  "salesforce_aptotude_5818",
]);

// Per-county public-portal links. Open in a new tab; the user does the lookup
// in their browser (which sails past Cloudflare / token gates that block
// programmatic access).
const COUNTY_PORTALS = {
  "Santa Clara County": (apn) =>
    `https://www.parcelquest.com/county/santa-clara/property/${encodeURIComponent(apn || "")}`,
  "Santa Clara County (assessor)": () => `https://asr.santaclaracounty.gov/`,
  "Alameda County": () => `https://propertytax.alamedacountyca.gov/search`,
  "Contra Costa County": () => `https://taxcolp.cccounty.us/taxpaymentrev3/lookup`,
  "San Mateo County": () => `https://propertytax.smcacre.org/index.php?option=com_search`,
  "Sonoma County": () => `https://ascr.sonoma-county.org/Pages/SearchProperty.aspx`,
  "Solano County": () => `https://gis.solanocounty.com/`,
  "Sacramento County": () => `https://eproptax.saccounty.gov/`,
  "Stanislaus County": () => `https://www.stancounty.com/assessor/parcel_lookup.shtm`,
  "Marin County": () => `https://prodseproptax.marincounty.org/`,
  "Merced County": () => `https://www.co.merced.ca.us/192/Property-Search`,
  "Placer County": () => `https://eppp.placer.ca.gov/`,
  "San Francisco County": () => `https://propertymap.sfplanning.org/`,
};

const portalUrlFor = (county, apn) => {
  if (!county) return null;
  const trimmed = String(county).trim();
  const builder = COUNTY_PORTALS[trimmed];
  if (!builder) return null;
  return builder(apn);
};

const contactDisplayName = (c) => {
  if (!c) return "";
  const p = c?.identity?.person || {};
  const name = `${p.firstName || ""} ${p.lastName || ""}`.replace(/\s+/g, " ").trim();
  const co = c?.identity?.company?.name;
  if (name && co) return `${name} (${co})`;
  return name || co || "(no name)";
};

const sourceLabelList = (c) => {
  const arr = Array.isArray(c?.data?.sources) ? c.data.sources : [];
  return arr.map((s) => String(s));
};

const PropertyVerifyPanel = ({ property, onClose, onResolved }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyById, setBusyById] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const apn = property?.identity?.apn?.value || property?.identity?.apn || "";
  const county = property?.identity?.address?.county || "";
  const portalUrl = portalUrlFor(county, apn);

  const provenance = property?.data?.verifyProvenance || {};
  const fmRecord = provenance?.fmRecord;
  const fmContactId = provenance?.fmContactId;
  const suggestedOwner = provenance?.suggestedOwner;

  const contactIds = useMemo(() => {
    const list = Array.isArray(property?.contactList) ? property.contactList : [];
    return Array.from(
      new Set(list.map((e) => String(e?.contactId || "")).filter(Boolean)),
    );
  }, [property]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (contactIds.length === 0) {
        setContacts([]);
        return;
      }
      setLoading(true);
      try {
        const result = await apiGetContactsCollection({
          // Verify merges and WRITES contact.data (sources/provenance) --
          // it must fetch full documents, not the slim card fragment.
          full: true,
          filters: { ids: contactIds },
          pagination: { page: 1, limit: Math.max(50, contactIds.length) },
          ordering: { field: "updatedAt", order: "Desc" },
        });
        if (cancelled) return;
        setContacts(Array.isArray(result?.items) ? result.items : []);
      } catch (err) {
        console.error("Failed to load contacts for verify panel:", err);
        if (!cancelled) setContacts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [contactIds.join("|"), refreshKey]);

  const handleConfirm = async (contact) => {
    if (!contact?._id) return;
    setBusyById((m) => ({ ...m, [contact._id]: "confirm" }));
    try {
      const currentSources = Array.isArray(contact?.data?.sources) ? contact.data.sources : [];
      const nextSources = Array.from(new Set([...currentSources, "user_verified"]));
      const currentData = (contact?.data && typeof contact.data === "object") ? contact.data : {};
      const { verifyProvenance, ...restData } = currentData;
      const nextVerifyProv = { ...(verifyProvenance || {}) };
      delete nextVerifyProv.needsCountyVerification;
      delete nextVerifyProv.needsCountyVerificationAt;
      delete nextVerifyProv.needsCountyVerificationReason;
      nextVerifyProv.confirmedAt = new Date().toISOString();
      nextVerifyProv.confirmedBy = "user_verified";
      await apiUpdateContact({
        id: String(contact._id),
        data: {
          ...restData,
          sources: nextSources,
          verifyProvenance: nextVerifyProv,
        },
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Confirm failed:", err);
    } finally {
      setBusyById((m) => {
        const c = { ...m };
        delete c[contact._id];
        return c;
      });
    }
  };

  const handleDetach = async (contact) => {
    if (!contact?._id || !property?.id) return;
    setBusyById((m) => ({ ...m, [contact._id]: "detach" }));
    try {
      const list = Array.isArray(property?.contactList) ? property.contactList : [];
      const nextList = list.filter((e) => String(e?.contactId) !== String(contact._id));
      await apiUpdatePropertyAsset({
        id: String(property.id),
        contactList: nextList,
      });

      // Best-effort: remove this property from the contact's propertyListIds.
      const currentIds = Array.isArray(contact?.propertyListIds) ? contact.propertyListIds : [];
      const trimmedIds = currentIds.filter((pid) => String(pid) !== String(property.id));
      const currentData = (contact?.data && typeof contact.data === "object") ? contact.data : {};
      const nextVerifyProv = { ...(currentData?.verifyProvenance || {}) };
      nextVerifyProv.detachedAt = new Date().toISOString();
      nextVerifyProv.detachedFromProperty = String(property.id);
      nextVerifyProv.detachedReason = "user_detached_in_verify_panel";
      await apiUpdateContact({
        id: String(contact._id),
        propertyListIds: trimmedIds,
        data: { ...currentData, verifyProvenance: nextVerifyProv },
      });

      onResolved?.({ propertyId: property.id, contactId: contact._id, action: "detach" });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Detach failed:", err);
    } finally {
      setBusyById((m) => {
        const c = { ...m };
        delete c[contact._id];
        return c;
      });
    }
  };

  const handleClearVerify = async () => {
    if (!property?.id) return;
    const prev = provenance?.previousState || "active";
    setBusyById((m) => ({ ...m, _property: "clearing" }));
    try {
      await apiUpdatePropertyAsset({
        id: String(property.id),
        state: prev,
      });
      onResolved?.({ propertyId: property.id, action: "clear" });
    } catch (err) {
      console.error("Clear verify failed:", err);
    } finally {
      setBusyById((m) => {
        const c = { ...m };
        delete c._property;
        return c;
      });
    }
  };

  if (!property) {
    return (
      <AddressbookPanelShell onClose={onClose} collectionLabel="Verify Property">
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            Select a property to verify.
          </Typography>
        </Box>
      </AddressbookPanelShell>
    );
  }

  const addr = property?.identity?.address || {};
  const addrLine = [addr.streetNo, addr.streetName, addr.city, addr.state, addr.zip].filter(Boolean).join(" ");
  const isVerify = String(property?.state || "").toLowerCase() === "verify";

  return (
    <AddressbookPanelShell
      onClose={onClose}
      collectionLabel="Verify Property"
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {isVerify ? "state: verify" : `state: ${property.state || "active"}`}
        </Typography>
      }
      headerActions={
        <Tooltip title="Reload contacts">
          <IconButton size="small" onClick={() => setRefreshKey((k) => k + 1)} sx={{ width: 24, height: 24 }}>
            <RefreshIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
            {addrLine || "(no address)"}
          </Typography>
          <Typography sx={{ fontSize: 11, color: "#64748B" }}>
            APN: {String(apn || "(none)")} · County: {county || "(unset)"}
          </Typography>
        </Box>

        {portalUrl ? (
          <Button
            startIcon={<OpenIcon sx={{ fontSize: 14 }} />}
            size="small"
            variant="outlined"
            href={portalUrl}
            target="_blank"
            rel="noreferrer noopener"
            sx={{ textTransform: "none", justifyContent: "flex-start" }}
          >
            Open {county} portal to verify owner
          </Button>
        ) : (
          <Typography sx={{ fontSize: 11, color: "#94A3B8", fontStyle: "italic" }}>
            No portal URL configured for {county || "(unset)"}.
          </Typography>
        )}

        <Box
          sx={{
            border: "1px solid #FDE68A",
            backgroundColor: "#FFFBEB",
            borderRadius: 1.5,
            p: 1,
          }}
        >
          <Typography sx={{ fontSize: 10, color: "#92400E", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
            FileMaker owner (record #{fmRecord || "?"})
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#1F2937", mt: 0.5 }}>
            {fmContactId
              ? `Attached as contact ${String(fmContactId).slice(0, 8)}…`
              : "(not stamped — older audit)"}
          </Typography>
          {suggestedOwner ? (
            <Typography sx={{ fontSize: 11, color: "#1F2937", mt: 0.25 }}>
              County (SM ArcGIS) suggested owner: <strong>{suggestedOwner.name}</strong>
            </Typography>
          ) : null}
        </Box>

        <Divider sx={{ my: 0.25 }} />

        <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>
          Contacts on this property
        </Typography>

        {loading ? (
          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {contacts.map((c) => {
              const id = String(c?._id || "");
              const sources = sourceLabelList(c);
              const isTrusted = sources.some((s) => TRUSTED_SOURCES.has(String(s).toLowerCase()));
              const isBusy = Boolean(busyById[id]);
              const flaggedForCounty = Boolean(c?.data?.verifyProvenance?.needsCountyVerification);
              return (
                <Box
                  key={id || c?.id || JSON.stringify(c)}
                  sx={{
                    border: "1px solid #E5E7EB",
                    borderRadius: 1.5,
                    p: 1,
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#111827", flex: 1, minWidth: 0 }} noWrap>
                      {contactDisplayName(c)}
                    </Typography>
                    {flaggedForCounty ? (
                      <Chip
                        label="needs county verify"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#92400E",
                          backgroundColor: "#FEF3C7",
                          border: "1px solid #F59E0B",
                        }}
                      />
                    ) : null}
                  </Box>

                  <Box sx={{ mt: 0.5, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {sources.length === 0 ? (
                      <Chip
                        label="no source"
                        size="small"
                        sx={{ height: 18, fontSize: 10, color: "#475569", backgroundColor: "#F1F5F9", border: "1px solid #CBD5E1" }}
                      />
                    ) : (
                      sources.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 10,
                            color: TRUSTED_SOURCES.has(String(s).toLowerCase()) ? "#065F46" : "#475569",
                            backgroundColor: TRUSTED_SOURCES.has(String(s).toLowerCase()) ? "#D1FAE5" : "#F1F5F9",
                            border: `1px solid ${TRUSTED_SOURCES.has(String(s).toLowerCase()) ? "#10B981" : "#CBD5E1"}`,
                          }}
                        />
                      ))
                    )}
                  </Box>

                  <Box sx={{ mt: 0.75, display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                      disabled={isBusy}
                      onClick={() => handleConfirm(c)}
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        color: "#065F46",
                        borderColor: "#10B981",
                        "&:hover": { backgroundColor: "#D1FAE5", borderColor: "#10B981" },
                      }}
                      variant="outlined"
                    >
                      Confirm
                    </Button>
                    <Button
                      size="small"
                      startIcon={<DetachIcon sx={{ fontSize: 14 }} />}
                      disabled={isBusy}
                      onClick={() => handleDetach(c)}
                      sx={{
                        textTransform: "none",
                        fontSize: 11,
                        color: "#991B1B",
                        borderColor: "#EF4444",
                        "&:hover": { backgroundColor: "#FEE2E2", borderColor: "#EF4444" },
                      }}
                      variant="outlined"
                    >
                      Detach
                    </Button>
                  </Box>
                </Box>
              );
            })}
            {contacts.length === 0 && (
              <Typography sx={{ fontSize: 12, color: "#64748B", textAlign: "center", py: 2 }}>
                No contacts attached.
              </Typography>
            )}
          </Box>
        )}

        {isVerify ? (
          <Button
            size="small"
            variant="contained"
            disabled={Boolean(busyById._property)}
            onClick={handleClearVerify}
            sx={{ textTransform: "none", mt: 1 }}
          >
            Mark this property verified (clear state)
          </Button>
        ) : null}
      </Box>
    </AddressbookPanelShell>
  );
};

export default PropertyVerifyPanel;
